import React from "react";
import { ArrowDownUp, Clock, Bell, Copy } from "lucide-react";
import Toggle from "../../ui/Toggle";

// ─── Types ────────────────────────────────────────────────────────────────────

interface WizardData {
  matterId: string;
  templateId: string;
  subject: string;
  message: string;
  documents: any[];
  signers: any[];
  fields: any[];
  sequentialSigning: boolean;
  expirationDays: number;
  reminders: { enabled: boolean; intervalDays: number; maxReminders: number };
  saveAsTemplate: boolean;
  templateName: string;
}

interface OptionsStepProps {
  data: WizardData;
  onChange: (data: WizardData) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const OptionsStep: React.FC<OptionsStepProps> = ({ data, onChange }) => {
  return (
    <div className="space-y-4">
      {/* Signing Order */}
      <div className="bg-slate-50 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
            <ArrowDownUp className="h-4.5 w-4.5 text-blue-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-700">
                  Require Sequential Signing
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Signers must sign in order. Signer 1 must complete before
                  Signer 2 is notified.
                </p>
              </div>
              <Toggle
                checked={data.sequentialSigning}
                onChange={(val) =>
                  onChange({ ...data, sequentialSigning: val })
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* Expiration */}
      <div className="bg-slate-50 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Clock className="h-4.5 w-4.5 text-amber-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-700">
                  Expiration
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  The signing request will expire after this many days
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={data.expirationDays}
                  onChange={(e) =>
                    onChange({
                      ...data,
                      expirationDays: parseInt(e.target.value) || 30,
                    })
                  }
                  min={1}
                  max={365}
                  className="w-20 rounded-xl border border-slate-200 px-3 py-2 text-sm text-center focus:outline-none focus:border-emerald-600 transition-colors"
                />
                <span className="text-xs text-slate-500">days</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reminders */}
      <div className="bg-slate-50 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Bell className="h-4.5 w-4.5 text-purple-600" />
          </div>
          <div className="flex-1 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-700">
                  Send Automatic Reminders
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Automatically remind signers who haven't completed signing
                </p>
              </div>
              <Toggle
                checked={data.reminders.enabled}
                onChange={(val) =>
                  onChange({
                    ...data,
                    reminders: { ...data.reminders, enabled: val },
                  })
                }
              />
            </div>

            {data.reminders.enabled && (
              <div className="pt-2 border-t border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600">Remind every</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={data.reminders.intervalDays}
                      onChange={(e) =>
                        onChange({
                          ...data,
                          reminders: {
                            ...data.reminders,
                            intervalDays: parseInt(e.target.value) || 3,
                          },
                        })
                      }
                      min={1}
                      max={30}
                      className="w-16 rounded-xl border border-slate-200 px-3 py-2 text-sm text-center focus:outline-none focus:border-emerald-600 transition-colors"
                    />
                    <span className="text-xs text-slate-500">days</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600">Maximum reminders</p>
                  <input
                    type="number"
                    value={data.reminders.maxReminders}
                    onChange={(e) =>
                      onChange({
                        ...data,
                        reminders: {
                          ...data.reminders,
                          maxReminders: parseInt(e.target.value) || 5,
                        },
                      })
                    }
                    min={1}
                    max={20}
                    className="w-16 rounded-xl border border-slate-200 px-3 py-2 text-sm text-center focus:outline-none focus:border-emerald-600 transition-colors"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save as Template */}
      <div className="bg-slate-50 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Copy className="h-4.5 w-4.5 text-emerald-600" />
          </div>
          <div className="flex-1 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-700">
                  Save as Reusable Template
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Save these settings as a template for future signing requests
                </p>
              </div>
              <Toggle
                checked={data.saveAsTemplate}
                onChange={(val) =>
                  onChange({ ...data, saveAsTemplate: val })
                }
              />
            </div>

            {data.saveAsTemplate && (
              <div className="pt-2 border-t border-slate-200">
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={data.templateName}
                  onChange={(e) =>
                    onChange({ ...data, templateName: e.target.value })
                  }
                  placeholder="E.g., Standard Retainer Agreement"
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OptionsStep;
